import React from "react";
import { Alert, Box, ButtonBase, Chip, Link, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HPInput from "@/components/common/Input";
import type { DatasetMetric } from "@models/Metric";
import {
  describeMetricFocus,
  formatMetricPopulationLabel,
  getMetricResultTypeLabel,
} from "@utils/metric-source-utils";
import {
  filterAndSortDatasetMetrics,
  isMetricEligibleForBrandMapping,
} from "@components/charts/scorecard-config/dataset-metric-row-utils";

interface DatasetMetricPickerListProps {
  datasetId: string | null;
  metrics: DatasetMetric[];
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  selectedMetricId: string;
  onSelectMetric: (metric: DatasetMetric) => void;
}

function getSourceShapeLabel(metric: DatasetMetric): string {
  switch (metric.source?.kind) {
    case "single_one_dimensional":
      return "Single";
    case "multiple_one_dimensional":
      return "Multiple";
    case "single_grid_value_focus":
    case "single_grid_variable_focus":
      return "Single grid";
    case "numeric":
      return "Number/Float";
    default:
      return "Unresolved";
  }
}

/** Search-and-list metric picker for the Metric row source — mirrors the
 *  question picker's search-and-list pattern used elsewhere in the Scorecard
 *  row builder (SourceQuestionAccordion), but reads from the already-loaded
 *  `dataset.metrics` array in memory rather than a paginated API, since the
 *  full catalog for a dataset is small and already fetched by the drawer. */
const DatasetMetricPickerList: React.FC<DatasetMetricPickerListProps> = ({
  datasetId,
  metrics,
  searchTerm,
  onSearchTermChange,
  selectedMetricId,
  onSelectMetric,
}) => {
  const results = filterAndSortDatasetMetrics(metrics, searchTerm);

  return (
    <Box>
      <HPInput
        label=""
        value={searchTerm}
        onChange={(e) => onSearchTermChange(e.target.value)}
        placeholder="Search metrics by name or source question..."
      />

      <Box sx={{ mt: 1 }}>
        {results.length === 0 ? (
          <Alert severity="info">
            No compatible metrics found in this dataset yet.{" "}
            {datasetId && (
              <Link
                href={`/datasets/${datasetId}/config`}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                Manage Metrics in Dataset Configuration
                <OpenInNewIcon sx={{ fontSize: 12 }} />
              </Link>
            )}
          </Alert>
        ) : (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              maxHeight: 320,
              overflow: "auto",
            }}
          >
            {results.map((metric) => {
              const { eligible, reason } = isMetricEligibleForBrandMapping(metric);
              const isSelected = metric.id === selectedMetricId;
              return (
                <ButtonBase
                  key={metric.id}
                  disabled={!eligible}
                  onClick={() => onSelectMetric(metric)}
                  sx={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    px: 1.5,
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor: isSelected ? "action.selected" : "transparent",
                    "&:last-child": { borderBottom: "none" },
                    "&:hover": {
                      backgroundColor: isSelected
                        ? "action.selected"
                        : "var(--color-hp-background-hover, #F5F5F5)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, width: "100%" }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {metric.name}
                    </Typography>
                    {metric.source && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                      >
                        {metric.source.question.questionLabel}
                        {" · "}
                        {getMetricResultTypeLabel(metric.source.resultType)}
                        {" · "}
                        {getSourceShapeLabel(metric)}
                        {" · "}
                        {describeMetricFocus(metric.source)}
                        {" · "}
                        {formatMetricPopulationLabel(metric.population)}
                      </Typography>
                    )}
                    {!eligible && reason && (
                      <Chip
                        label={reason}
                        size="small"
                        color="default"
                        sx={{ height: "auto", mt: 0.5, "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 } }}
                      />
                    )}
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DatasetMetricPickerList;
